import { motion } from "framer-motion";

const BENEFITS = [
  { k: "24/7", label: "Design Advisory" },
  { k: "Global", label: "Sourcing Network" },
  { k: "Dedicated", label: "Personal Concierge" },
  { k: "Lifetime", label: "Relationship" },
  { k: "Private", label: "Trend Forecasting" },
];

export default function VIP() {
  return (
    <section
      id="vip"
      data-testid="vip-section"
      className="relative text-cream overflow-hidden"
      style={{
        background:
          "linear-gradient(180deg, #050604 0%, #020301 30%, #020301 70%, #050604 100%)",
      }}
    >
      {/* Backdrop — white-gloved butler presenting the studio card */}
      <div aria-hidden className="absolute inset-0 z-0">
        <img
          src="/assets/vip-backdrop.png"
          alt=""
          className="w-full h-full object-cover"
          style={{
            filter: "grayscale(100%) contrast(1.08) brightness(0.68)",
            objectPosition: "right center",
          }}
          draggable={false}
        />
        {/* Left-to-right hard fade so the copy on the left sits on pure black,
            the butler + card float in the right third */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, rgba(3,4,2,1) 0%, rgba(3,4,2,0.98) 25%, rgba(3,4,2,0.72) 48%, rgba(3,4,2,0.30) 68%, rgba(3,4,2,0.20) 100%)",
          }}
        />
        {/* Top + bottom feather so the image edges disappear into the page */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(3,4,2,0.85) 0%, rgba(3,4,2,0) 20%, rgba(3,4,2,0) 78%, rgba(3,4,2,0.95) 100%)",
          }}
        />
        {/* Subtle brass warmth on the card */}
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 32% 24% at 78% 62%, rgba(176,140,74,0.18), transparent 70%)",
            mixBlendMode: "screen",
          }}
        />
      </div>

      {/* Ambient brass glow */}
      <div
        aria-hidden
        className="absolute inset-0 z-0"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 15% 20%, rgba(176,140,74,0.14), transparent 60%), radial-gradient(ellipse 60% 40% at 85% 90%, rgba(176,140,74,0.08), transparent 60%)",
        }}
      />
      <div className="absolute inset-0 grain opacity-30 z-0" />

      <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-10 py-28 md:py-40">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          <div className="md:col-span-7">
            <div className="flex items-center gap-3 text-brass mb-8">
              <span className="w-8 h-px bg-brass" />
              <span className="text-[10px] md:text-[11px] tracking-overline uppercase font-medium">
                Bespoke · Invitation Only
              </span>
            </div>

            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15% 0px" }}
              transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
              className="font-serif font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-editorial leading-[0.94] max-w-[16ch]"
            >
              The <span className="italic text-brass">Bespoke</span><br />
              VIP Service.
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-15% 0px" }}
              transition={{ duration: 1, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
              className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-cream/70 font-light"
            >
              A white‑glove relationship for a small circle of patrons.
              Unfettered access to our atelier, our artisans and our judgement —
              for the moments that matter, and the ones no one else sees.
            </motion.p>
          </div>

          {/* Right: subtle framed number */}
          <div className="md:col-span-4 md:col-start-9 flex md:justify-end">
            <div className="border border-brass/25 px-8 py-10 max-w-[280px]">
              <div className="text-[10px] tracking-overline uppercase text-brass mb-4">
                Availability
              </div>
              <div className="font-serif text-6xl md:text-7xl text-cream leading-none">
                7<span className="text-brass">/</span>yr
              </div>
              <p className="mt-4 text-cream/60 text-sm leading-relaxed">
                Seven new patrons a year. Reviewed personally by Rishabh.
              </p>
            </div>
          </div>
        </div>

        {/* Benefits */}
        <div className="mt-20 md:mt-28 grid grid-cols-2 md:grid-cols-5 gap-y-10 md:gap-x-8 border-t border-cream/12 pt-14">
          {BENEFITS.map((b, i) => (
            <motion.div
              key={b.label}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10% 0px" }}
              transition={{ duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] }}
              data-testid={`vip-benefit-${b.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <div className="font-serif italic text-3xl md:text-4xl text-brass mb-3">
                {b.k}
              </div>
              <div className="text-[11px] tracking-overline uppercase text-cream/70">
                {b.label}
              </div>
            </motion.div>
          ))}
        </div>

        <div className="mt-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-10 border-t border-cream/12">
          <p className="text-sm md:text-base text-cream/60 max-w-xl font-light leading-relaxed">
            The VIP circle is closed and considered. Admission is by referral
            or personal invitation.
          </p>
          <a
            href="#contact"
            data-testid="vip-request-cta"
            className="group inline-flex items-center gap-3 self-start text-[11px] font-sans tracking-overline uppercase text-onyx bg-brass hover:bg-brass-bright transition-colors duration-500 px-6 py-4"
          >
            Request an Introduction
            <span className="transition-transform duration-500 group-hover:translate-x-1">→</span>
          </a>
        </div>
      </div>
    </section>
  );
}
